import {
  CheckCircleOutlined,
  EllipsisOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import { Button, Dropdown, Input, Radio, Select, Switch, Table } from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useBulkDeleteCouponsMutation,
  useBulkUpdateCouponStatusMutation,
  useDeleteCouponMutation,
  useGetCouponsQuery,
  useUpdateCouponMutation,
} from '@/modules/coupons/couponsApi';
import type { Coupon, CouponDiscountType, CouponStatusFilter } from '@/types/coupon';
import { COUPON_DISCOUNT_TYPE_OPTIONS } from '@/types/coupon';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmBulkDelete, confirmDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { useCouponQueryParams } from './hooks/useCouponQueryParams';
import styles from './Coupons.module.scss';

function formatDiscount(coupon: Coupon): string {
  if (coupon.discountType === 'percentage') {
    return `${coupon.discountValue}%`;
  }
  return `₹${coupon.discountValue}`;
}

export default function Coupons() {
  const notify = useNotify();
  const navigate = useNavigate();
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } = useCouponQueryParams();
  const { data: listResult, isLoading, isFetching } = useGetCouponsQuery(apiParams);
  useGlobalLoader(isFetching && !!listResult);

  const coupons = listResult?.results ?? [];
  const activeSortBy = listResult?.sortBy ?? state.sortBy;
  const activeSortOrder = listResult?.sortOrder ?? state.sortOrder;
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const [searchDraft, setSearchDraft] = useState(state.search);
  const [statusDraft, setStatusDraft] = useState<CouponStatusFilter>(state.status);
  const [discountTypeDraft, setDiscountTypeDraft] = useState<CouponDiscountType | 'all'>(
    state.discountType,
  );

  const [updateCoupon] = useUpdateCouponMutation();
  const [deleteCoupon] = useDeleteCouponMutation();
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] = useBulkUpdateCouponStatusMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteCouponsMutation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const openEdit = useCallback(
    (coupon: Coupon) => {
      navigate(`/coupons/${coupon.id}/edit`);
    },
    [navigate],
  );

  const clearSelection = () => setSelectedRowKeys([]);

  const handleResetFiltersAndSort = () => {
    setSearchDraft('');
    setStatusDraft('all');
    setDiscountTypeDraft('all');
    resetAll();
  };

  const handleStatusChange = useCallback(
    async (coupon: Coupon, isActive: boolean) => {
      const result = await resolveMutationResult(
        updateCoupon({ id: coupon.id, body: { isActive } }),
      );

      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        return;
      }

      notify.error(result.message);
    },
    [notify, updateCoupon],
  );

  const handleDelete = useCallback(
    async (coupon: Coupon) => {
      const confirmed = await confirmDelete(coupon.code);
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteCoupon(coupon.id));
      if (result.ok) {
        notify.success(result.message ?? 'Coupon deleted.');
        setSelectedRowKeys((keys) => keys.filter((key) => key !== coupon.id));
        return;
      }

      notify.error(result.message);
    },
    [deleteCoupon, notify],
  );

  const handleBulkStatus = async (isActive: boolean) => {
    if (selectedRowKeys.length === 0) return;

    const result = await resolveMutationResult(
      bulkUpdateStatus({ ids: selectedRowKeys, isActive }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Coupons updated.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    const confirmed = await confirmBulkDelete(selectedRowKeys.length);
    if (!confirmed) return;

    const result = await resolveMutationResult(bulkDelete({ ids: selectedRowKeys }));

    if (result.ok) {
      notify.success(result.message ?? 'Coupons deleted.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleTableChange: TableProps<Coupon>['onChange'] = (pagination) => {
    const pageChanged =
      pagination.current !== undefined && pagination.current !== state.page;
    const limitChanged =
      pagination.pageSize !== undefined && pagination.pageSize !== state.limit;

    if (!pageChanged && !limitChanged) return;

    patchParams({
      ...(pageChanged && pagination.current !== undefined ? { page: pagination.current } : {}),
      ...(limitChanged && pagination.pageSize !== undefined ? { limit: pagination.pageSize } : {}),
    });
  };

  const getRowActionItems = useCallback(
    (coupon: Coupon): MenuProps['items'] => [
      { key: 'edit', label: 'Edit', onClick: () => openEdit(coupon) },
      { type: 'divider' },
      { key: 'delete', label: 'Delete', danger: true, onClick: () => handleDelete(coupon) },
    ],
    [handleDelete, openEdit],
  );

  const columns: ColumnsType<Coupon> = useMemo(
    () => [
      {
        title: (
          <SortableColumnTitle
            label="CODE"
            field="code"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'code',
        key: 'code',
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.search ? [state.search] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setSearchDraft('');
              patchParams({ search: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search code or description"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onPressEnter={() => {
                patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setSearchDraft(state.search);
          },
        },
        render: (code: string) => <span className={styles.codeCell}>{code}</span>,
      },
      {
        title: 'DESCRIPTION',
        dataIndex: 'description',
        key: 'description',
        className: styles.descriptionCol,
        render: (value: string) => (
          <span className={styles.descriptionCell} title={value}>
            {value || '—'}
          </span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="DISCOUNT"
            field="discountType"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        key: 'discount',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.discountType !== 'all' ? [state.discountType] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { discountType: discountTypeDraft === 'all' ? null : discountTypeDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setDiscountTypeDraft('all');
              patchParams({ discountType: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              size="small"
              value={discountTypeDraft}
              onChange={setDiscountTypeDraft}
              style={{ width: '100%' }}
              options={[
                { label: 'All types', value: 'all' },
                ...COUPON_DISCOUNT_TYPE_OPTIONS,
              ]}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setDiscountTypeDraft(state.discountType);
          },
        },
        render: (_value, record) => (
          <span className={styles.discountCell}>{formatDiscount(record)}</span>
        ),
      },
      {
        title: 'USAGE',
        key: 'usage',
        render: (_value, record) => (
          <span className={styles.usageCell}>
            {record.usageCount}
            {record.usageLimit != null ? ` / ${record.usageLimit}` : ''}
          </span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="STATUS"
            field="isActive"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'isActive',
        key: 'isActive',
        align: 'center',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.status !== 'all' ? [state.status] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ status: statusDraft === 'all' ? null : statusDraft }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setStatusDraft('all');
              patchParams({ status: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
              className={styles.statusRadio}
            >
              <Radio value="all">All</Radio>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setStatusDraft(state.status);
          },
        },
        render: (isActive: boolean, record) => (
          <Switch
            checked={isActive}
            size="small"
            onChange={(checked) => handleStatusChange(record, checked)}
          />
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="UPDATED"
            field="updatedAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value: string) => <span className={styles.dateCell}>{formatDateTime(value)}</span>,
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_value, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button type="text" size="small" icon={<EllipsisOutlined />} className={styles.actionBtn} />
          </Dropdown>
        ),
      },
    ],
    [
      activeSortBy,
      activeSortOrder,
      discountTypeDraft,
      getRowActionItems,
      handleSort,
      handleStatusChange,
      patchParams,
      searchDraft,
      state.discountType,
      state.search,
      state.status,
      statusDraft,
    ],
  );

  const rowSelection: TableRowSelection<Coupon> = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFiltersOrSort ? (
              <Button
                type="default"
                size="small"
                className={styles.headerBtn}
                icon={<ReloadOutlined />}
                onClick={handleResetFiltersAndSort}
              >
                Reset
              </Button>
            ) : null}
            <Button
              type="primary"
              size="small"
              className={styles.addBtn}
              icon={<PlusOutlined />}
              onClick={() => navigate(ROUTES.COUPON_CREATE)}
            >
              Add coupon
            </Button>
          </>
        }
      />

      {selectedRowKeys.length > 0 ? (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            loading={isBulkUpdating}
            onClick={() => handleBulkStatus(true)}
          >
            Activate
          </Button>
          <Button
            size="small"
            icon={<StopOutlined />}
            loading={isBulkUpdating}
            onClick={() => handleBulkStatus(false)}
          >
            Deactivate
          </Button>
          <Button size="small" danger loading={isBulkDeleting} onClick={handleBulkDelete}>
            Delete
          </Button>
          <Button size="small" type="link" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        <Table<Coupon>
          className={styles.table}
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={coupons}
          loading={isLoading && !listResult}
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (value) => `${value} coupons`,
            size: 'small',
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
}
